<!-- バーコード指定商品一覧画面の実装 -->
<template>
  <MainLayout>
    <ContentPageHeader functionName="商品管理" pageName="バーコード指定商品一覧">
    </ContentPageHeader>
    
    <!-- 基本セクション -->
    <div class="search-section">
      <div class="search-form">
        <FormTextField 
          label="商品コード" 
          id="productCode" 
          v-model="searchParams.productCode"
          size="medium"
        />
      </div>
    </div>
    
    <!-- 一括操作ボタン -->
    <div class="batch-operations">
      <button class="btn btn-publish-registration" @click="bulkPublish">公開登録</button>
      <button class="btn btn-secondary" @click="printLabels">ラベル印刷</button>
      <button class="btn btn-primary" @click="showPriceChangeModal">価格変更</button>
      <button class="btn btn-primary" @click="showRankChangeModal">ランク変更</button>
      <button v-if="userAuthority !== 3" class="btn btn-danger" @click="bulkDelete">削除</button>
    </div>
    
    <!-- 価格変更モーダル -->
    <common-modal
      v-if="priceChangeModalVisible"
      :visible="priceChangeModalVisible"
      title="価格変更"
      @close="closePriceChangeModal"
      @ok="confirmPriceChange"
    >
      <div class="price-change-selector">
        <table class="price-change-table">
          <thead></thead>
          <tbody>
            <tr>
              <td class="label-cell">
                <label class="form-label">値下げ区分</label>
              </td>
              <td class="input-cell">
                <select v-model="selectedPriceChangeType" class="form-control">
                  <option value="percent">％</option>
                  <option value="fixed">円</option>
                </select>
              </td>
            </tr>
            <tr>
              <td class="label-cell">
                <!-- 空のラベル -->
              </td>
              <td class="input-cell">
                <div v-if="selectedPriceChangeType === 'percent'">
                  <select v-model="priceChangeValue" class="form-control">
                    <option value="-5">-5</option>
                    <option value="-10">-10</option>
                    <option value="-15">-15</option>
                    <option value="-20">-20</option>
                    <option value="-30">-30</option>
                    <option value="-50">-50</option>
                  </select>
                </div>
                <div v-else-if="selectedPriceChangeType === 'fixed'">
                  <input 
                    type="number" 
                    v-model="priceChangeValue" 
                    class="form-control"
                    placeholder="数値を入力してください"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </common-modal>
    
    <!-- 公開ステータス選択モーダル -->
    <common-modal
      v-if="publicStatusModalVisible"
      :visible="publicStatusModalVisible"
      title="公開ステータス選択"
      @close="closePublicStatusModal"
      @ok="confirmPublicStatusChange"
    >
      <div class="public-status-selector">
        <table class="public-status-table">
          <thead></thead>
          <tbody>
            <tr>
              <td class="label-cell">
                <label class="form-label">公開ステータス</label>
              </td>
              <td class="input-cell">
                <select v-model="selectedPublicStatusId" class="form-control">
                  <option v-for="option in publicationStatusOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </td>
            </tr>
            <tr v-if="isPublicStatusSold">
              <td class="label-cell">
                <label class="form-label">販売チャンネル</label>
              </td>
              <td class="input-cell">
                <select v-model="selectedSalesChannelId" class="form-control">
                  <option v-for="option in salesChannelOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </common-modal>
    
    <!-- ランク変更モーダル -->
    <common-modal
      v-if="rankChangeModalVisible"
      :visible="rankChangeModalVisible"
      title="ランク変更"
      @close="closeRankChangeModal"
      @ok="confirmRankChange"
    >
      <div class="rank-selector-options">
        <div v-for="rank in ranks" :key="rank.id" class="rank-selector-option">
          <input
            type="radio"
            :id="`rank_${rank.id}`"
            name="rank"
            :value="rank.id"
            v-model="selectedRankId"
          />
          <label :for="`rank_${rank.id}`" class="bold-label">{{ rank.rank }}</label>
        </div>
      </div>
    </common-modal>
    
    <!-- カラム選択モーダル -->
    <div v-if="columnSelectorVisible" class="column-selector-modal">
      <div class="column-selector-content">
        <div class="column-selector-header">
          <h4>表示する行</h4>
          <button class="close-button" @click="toggleColumnSelector">&times;</button>
        </div>
        <div class="column-selector-options">
          <div v-for="column in selectableColumns" :key="column.key" class="column-selector-option">
            <input 
              type="checkbox" 
              :id="`column-selector-${column.key}`" 
              :value="column.key" 
              v-model="visibleColumns"
              @change="onVisibleColumnsChange(visibleColumns)"
            />
            <label :for="`column-selector-${column.key}`">{{ column.label }}</label>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 検索結果テーブル -->
    <div class="search-result">
      <div class="search-result-header">
        <h4>検索結果</h4>
        <!-- 表示設定ボタン -->
        <button class="btn btn-outline-secondary" @click="toggleColumnSelector">表示設定</button>
      </div>
      <FormListTable
        :items="sortedProducts"
        :columns="columns"
        :actions="actions"
        :perPage="perPage"
        :page="currentPage"
        :totalItems="totalCount"
        :emptyMessage="''"
        :visibleColumns="visibleColumns"
        @page-change="onPageChange"
        @action="onAction"
      >
        <!-- 全選択チェックボックス用のヘッダーテンプレート -->
        <template #header-select>
          <div class="column-header">
            <input type="checkbox" v-model="isAllSelected" @change="toggleSelectAll" />
          </div>
        </template>
        
        <!-- 個別選択チェックボックス -->
        <template #select="{ item }">
          <input type="checkbox" v-model="selectedProducts" :value="item.id" />
        </template>
        
        <!-- カスタムヘッダーテンプレート -->
        <template v-for="col in columns.filter(col => col.sortable)" :key="`header-${col.key}`" #[`header-${col.key}`]>
          <div class="column-header" @click="sortBy(col.key, col.originalKey)">
            {{ col.label }}
            <span class="sort-icon">
              <i v-if="sortKey === col.key && sortOrder === 'asc'" class="fa fa-sort-up" aria-hidden="true"></i>
              <i v-else-if="sortKey === col.key && sortOrder === 'desc'" class="fa fa-sort-down" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true"></i>
            </span>
          </div>
        </template>
      </FormListTable>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import service from '../../services/service';
import printAgentService from '../../services/printAgentService';
import CommonModal from '../../components/common/CommonModal.vue';
import constants from '@/constants/constants.json';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'ProductBarcodeView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormListTable,
    CommonModal
  },
  data() {
    return {
      // 検索パラメータ
      searchParams: {
        productCode: '',
      },
      
      // ページング
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      
      // 選択された商品
      selectedProducts: [],
      
      // 価格変更モーダル
      priceChangeModalVisible: false,
      selectedPriceChangeType: 'percent',
      priceChangeValue: '',
      
      // 公開ステータス選択モーダル
      publicStatusModalVisible: false,
      selectedPublicStatusId: null,
      selectedSalesChannelId: "1",
      
      // ランク変更モーダル
      rankChangeModalVisible: false,
      selectedRankId: null,
      ranks: [],
      
      // メーカーオプション
      manufacturerOptions: [],
      
      // カテゴリオプション
      categoryOptions: [],
      
      // 公開ステータスオプション
      publicationStatusOptions: [],
      salesChannelOptions: [],
      
      // カテゴリの元データ（ランク連動用）
      originalCategories: [],
      
      // 店舗オプション
      storeOptions: [],
      
      // 商品ステータスオプション
      productStatusOptions: [],
      
      // テーブルカラム定義
      columns: [
        { key: 'select', label: '', sortable: false, minWidth: 40, maxWidth: 60 },
        { key: 'productCode', label: '商品コード', sortable: true, minWidth: 140, maxWidth: 180 },
        { key: 'publicationStatus', label: '公開状態', sortable: true, minWidth: 110, maxWidth: 140 },
        { key: 'purchaseDate', label: '買取日', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'registrationDate', label: '登録日', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'category', label: 'カテゴリ', sortable: true, originalKey: 'categoryId', minWidth: 180, maxWidth: 400 },
        { key: 'rank', label: 'ランク', sortable: true, originalKey: 'rankId', minWidth: 90, maxWidth: 110 },
        { key: 'manufacturer', label: 'メーカー', sortable: true, originalKey: 'manufacturerId', minWidth: 90, maxWidth: 450 },
        { key: 'productName', label: '商品名', sortable: true, minWidth: 100, maxWidth: 600 },
        { key: 'currentStore', label: '現在店舗', sortable: true, originalKey: 'currentStoreId', minWidth: 140, maxWidth: 180 },
        { key: 'stock', label: '在庫', sortable: true, minWidth: 40, maxWidth: 80 },
        { key: 'purchasePrice', label: '買取価格', sortable: true, minWidth: 120, maxWidth: 180 },
        { key: 'currentSellingPrice', label: '販売価格', sortable: true, minWidth: 120, maxWidth: 180 },
        { key: 'saleDate', label: '販売日', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'initialSellingPrice', label: '初期売価',sortable: true, minWidth: 120, maxWidth: 180 },
        { key: 'listingDateTime', label: '出品日時', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'profitMargin', label: '粗利率', sortable: true, minWidth: 100, maxWidth: 150 },
        { key: 'soldPrice', label: '売れた価格', sortable: true, minWidth: 120, maxWidth: 180 },
        { key: 'salesChannel', label: '販売チャンネル', sortable: true, minWidth: 120, maxWidth: 200 },
      ],
      
      // ソート状態
      sortKey: null,
      sortOrder: 'asc',
      currentSortOriginalKey: null, // マスタデータのID値でソートするための元キー
      
      // 表示カラム設定
      visibleColumns: [],
      
      // カラム選択モーダルの表示状態
      columnSelectorVisible: false,
      
      // 操作ボタン定義
      actions: [
        {
          label: '',
          icon: 'fa-edit',
          buttonClass: 'btn-primary',
          action: (item) => this.editProduct(item),
        }
      ],
      
      // 検索結果一覧（現在のページに表示する商品）
      products: [],
      
      // 全検索結果の蓄積リスト
      allProducts: []
    }
  },
  methods: {
    // 初期データ取得処理
    async getInitData() {
      showLoading();
      try {
        // API呼び出しでメーカー一覧を取得
        const makerResponse = await service.getMakerList();
        this.manufacturerOptions = makerResponse.data.makers.map(maker => {
          return { value: maker.id.toString(), label: maker.maker };
        });
        
        // API呼び出しでカテゴリ一覧を取得
        const categoryResponse = await service.getCategoryList();
        this.originalCategories = categoryResponse.data.categories;
        // カテゴリオプションを整形
        this.categoryOptions = [];
        for (const parent of this.originalCategories) {
          for (const child of parent.childCategories) {
            this.categoryOptions.push({
              value: child.id.toString(),
              label: `${parent.categoryType} - ${child.category}`
            });
          }
        }

        // API呼び出しで公開ステータス一覧を取得
        const publicStateResponse = await service.getPublicStateList();
        this.publicationStatusOptions = publicStateResponse.data.publicStates.map(state => {
          return { value: state.id, label: state.publicState };
        });

        // API呼び出しで店舗一覧を取得
        const storeResponse = await service.getStoreList();
        this.storeOptions = storeResponse.data.stores.map(store => {
          return { value: store.id.toString(), label: store.storeName };
        });
        
        // API呼び出しで販売チャンネル一覧を取得
        const salesChannelResponse = await service.getSalesChannelList();
        this.salesChannelOptions = salesChannelResponse.data.salesChannels.map(channel => {
          return { value: channel.id.toString(), label: channel.acc };
        });

        // API呼び出しで商品ステータス（タグ）一覧を取得
        const tagResponse = await service.getTagList();
        this.productStatusOptions = tagResponse.data.tags.map(tag => {
          return { value: tag.id, label: tag.tagName };
        });
      } catch (error) {
        console.error('初期情報の取得に失敗しました:', error);
        alert('初期情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    // 検索条件リセット
    resetSearch() {
      this.searchParams = {
        productCode: '',
      };
      
      this.search();
    },
    // 検索処理
    async search() {
      try {
        showLoading();
        // 選択されている商品の解除
        this.selectedProducts = [];
        // 検索パラメータの構築
        const params = new URLSearchParams();
        
        if (this.searchParams.productCode) {
          params.append('productCode', this.searchParams.productCode);
        }
        
        // APIリクエスト（ページングパラメータは不要、商品コードで検索するため）
        const response = await service.getProductList(params);
        if (response.data.success && 0 < response.data.products.length) {
          // 取得したデータを整形して表示用に変換
          const formattedProducts = this.formatProductsForDisplay(response.data.products);
          
          // 検索結果を蓄積リストに追加（重複チェック）
          for (const newProduct of formattedProducts) {
            // 商品コードが一致する商品が既に存在するかチェック
            const existingProductIndex = this.allProducts.findIndex(p => p.productCode === newProduct.productCode);
            
            // 存在しない場合のみ追加
            if (existingProductIndex !== -1) {
              alert('この商品は既に一覧に存在します');
              continue;
            }
            this.allProducts.push(newProduct);
          }
          
          // ページング情報を更新
          this.totalCount = this.allProducts.length;
          
          // 現在のページに表示する商品を設定
          this.updateCurrentPageProducts();
        } else if (response.data.success && response.data.products.length === 0) {
          alert('該当する商品が見つかりませんでした');
        } else {
          alert('商品検索に失敗しました');
        }
      } catch (error) {
        console.error('商品検索エラー:', error);
        alert('商品検索中にエラーが発生しました');
      } finally {
        // 入力フィールドをクリア
        this.searchParams.productCode = '';
        hideLoading();
      }
    },
    
    // 現在のページに表示する商品を更新
    updateCurrentPageProducts() {
      const startIndex = this.currentPage * this.perPage;
      const endIndex = startIndex + this.perPage;
      this.products = this.allProducts.slice(startIndex, endIndex);
    },
    
    // 表示用にデータを整形するメソッド
    formatProductsForDisplay(products) {
      return products.map(product => {
        // 日付フォーマットの変換
        const formattedProduct = { ...product };
        
        // 買取日のフォーマット変換（ハイフンをスラッシュに）
        if (formattedProduct.purchaseDate) {
          formattedProduct.purchaseDate = this.formatDate(formattedProduct.purchaseDate);
        }
        
        // 登録日のフォーマット変換（時間部分を削除してハイフンをスラッシュに）
        if (formattedProduct.registrationDate) {
          formattedProduct.registrationDate = this.formatDate(formattedProduct.registrationDate);
        }
        
        // 販売日のフォーマット変換（ハイフンをスラッシュに）
        if (formattedProduct.saleDate) {
          formattedProduct.saleDate = this.formatDate(formattedProduct.saleDate);
        }
        
        // 出品日時のフォーマット変換
        if (formattedProduct.listingDateTime) {
          formattedProduct.listingDateTime = this.formatDateTime(formattedProduct.listingDateTime);
        }
        
        // カテゴリIDをカテゴリ名に変換
        if (formattedProduct.categoryId) {
          formattedProduct.category = this.getCategoryNameById(formattedProduct.categoryId);
        }
        
        // ランクIDをランク名に変換
        if (formattedProduct.rankId) {
          formattedProduct.rank = this.getRankNameById(formattedProduct.rankId);
        } else if (formattedProduct.rank) {
          // rankIdがない場合でもrankがある場合は変換を試みる
          formattedProduct.rank = this.getRankNameById(formattedProduct.rank);
        }
        
        // メーカーIDをメーカー名に変換
        if (formattedProduct.manufacturer) {
          formattedProduct.manufacturer = this.getMakerNameById(formattedProduct.manufacturer);
        }

        // 公開ステータス名に変換
        if (formattedProduct.publicStateId) {
          formattedProduct.publicationStatus = this.getPublicStateNameById(formattedProduct.publicStateId);
        }
        
        // 金額フォーマット（買取価格）
        if (formattedProduct.purchasePrice) {
          formattedProduct.purchasePrice = this.formatCurrency(formattedProduct.purchasePrice);
        }

        // 金額フォーマット（販売価格）
        if (formattedProduct.currentSellingPrice) {
          formattedProduct.currentSellingPrice = this.formatCurrency(formattedProduct.currentSellingPrice);
        }
        
        // 店舗IDを店舗名に変換
        if (formattedProduct.currentStore) {
          formattedProduct.currentStore = this.getStoreNameById(formattedProduct.currentStore);
        }
        
        // 金額フォーマット（初期売価）
        if (formattedProduct.initialSellingPrice) {
          formattedProduct.initialSellingPrice = this.formatCurrency(formattedProduct.initialSellingPrice);
        }
        
        // 金額フォーマット（売れた価格）
        if (formattedProduct.soldPrice) {
          formattedProduct.soldPrice = this.formatCurrency(formattedProduct.soldPrice);
        }
        
        // 粗利率のフォーマット
        if (formattedProduct.profitMargin) {
          formattedProduct.profitMargin = this.formatPercentage(formattedProduct.profitMargin);
        }
        
        return formattedProduct;
      });
    },
    
    // 日付フォーマット変換（YYYY-MM-DD → YYYY/MM/DD）
    formatDate(dateString) {
      if (!dateString) return '';
      
      // ISO形式の日付から時間部分を削除
      const datePart = dateString.split('T')[0];
      
      // ハイフンをスラッシュに変換
      return datePart.replace(/-/g, '/');
    },
    
    // 日時フォーマット変換（YYYY-MM-DDTHH:MM:SS → YYYY/MM/DD HH:MM）
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      
      try {
        const date = new Date(dateTimeString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}/${month}/${day}`;
      } catch (e) {
        return dateTimeString;
      }
    },
    
    // 金額フォーマット（数値 → ¥XX,XXX）
    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      
      // 数値に変換
      const numValue = parseInt(value);
      if (isNaN(numValue)) return value;
      
      // 3桁区切りでフォーマット
      return '¥' + numValue.toLocaleString();
    },
    
    // パーセンテージフォーマット（数値 → XX.X%）
    formatPercentage(value) {
      if (value === null || value === undefined) return '';
      
      // 数値に変換
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;
      
      // 小数点第一位までフォーマット
      return numValue.toFixed(1) + '%';
    },
    
    // カテゴリIDからカテゴリ名を取得
    getCategoryNameById(categoryId) {
      const category = this.categoryOptions.find(option => option.value === categoryId.toString());
      return category ? category.label : categoryId;
    },
    
    // ランクIDからランク名を取得
    getRankNameById(rankId) {
      // 全カテゴリから全ランクを取得
      let allRanks = [];
      for (const parent of this.originalCategories) {
        for (const child of parent.childCategories) {
          allRanks = [...allRanks, ...child.ranks];
        }
      }
      
      const rank = allRanks.find(r => r.id.toString() === rankId.toString());
      return rank ? rank.rank : rankId;
    },
    
    // メーカーIDからメーカー名を取得
    getMakerNameById(makerId) {
      const maker = this.manufacturerOptions.find(option => option.value === makerId.toString());
      return maker ? maker.label : makerId;
    },

    // 公開ステータス名を取得
    getPublicStateNameById(publicStateId) {
      const publicState = this.publicationStatusOptions.find(option => option.value === publicStateId);
      return publicState ? publicState.label : '';
    },
    
    // 店舗IDから店舗名を取得
    getStoreNameById(storeId) {
      const store = this.storeOptions.find(option => option.value === storeId.toString());
      return store ? store.label : storeId;
    },
    
    // ページ変更ハンドラ
    onPageChange(page) {
      // ページ変更時にローカルページングを行う
      // FormListTableは1-indexedなので、0-indexedに変換する
      this.currentPage = page - 1;
      this.updateCurrentPageProducts();
    },
    
    // アクションハンドラ
    onAction({ action, item }) {
      if (action !== 'editProduct') return;
      this.editProduct(item);
    },
    
    // 商品編集画面へ遷移
    editProduct(item) {
      this.$router.push({ 
        name: 'ProductEdit', 
        params: { id: item.id },
        query: { source: 'barcode' } // バーコード指定商品一覧からの遷移を示す
      });
      // 画面遷移時にスクロール位置をリセット
      window.scrollTo(0, 0);
    },
    
    // カラムヘッダークリック時のソート処理
    sortBy(key, originalKey) {
      // 同じカラムをクリックした場合は昇順/降順を切り替え
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        // 異なるカラムをクリックした場合は昇順にリセット
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
      
      // マスタデータの場合は元のID値でソート
      this.currentSortOriginalKey = originalKey || null;
    },
    

    
    // 全選択/全解除の切り替え
    toggleSelectAll() {
      // isAllSelectedがtrueの場合は全解除、falseの場合は全選択
      if (this.isAllSelected) {
        // 全ての商品の選択を解除
        this.selectedProducts = [];
      } else {
        // 全ての商品を選択
        this.selectedProducts = this.sortedProducts.map(product => product.id);
      }
    },
    
    // 一括削除処理
    async bulkDelete() {
      // 選択された商品がない場合は処理を中止
      if (this.selectedProducts.length === 0) {
        alert('削除する商品を選択してください');
        return;
      }

      // 権限チェック
      if (this.userAuthority === 2) { // 店長の場合
        // 選択された商品が自店舗のものかチェック
        const nonOwnStoreProducts = this.products.filter(product => 
          this.selectedProducts.includes(product.id) && 
          product.currentStoreId !== this.userStoreId
        );
        
        if (0 < nonOwnStoreProducts.length) {
          alert('自店舗の商品のみ削除できます。他店舗の商品が選択されています。');
          return;
        }
      }
      
      // 確認ダイアログを表示
      const isConfirmed = confirm('一括削除します。よろしいですか。');
      if (!isConfirmed) {
        return; // キャンセルされた場合は処理を中止
      }
      
      try {
        showLoading();
        // 一括削除APIを呼び出し
        const response = await service.bulkDeleteProducts(this.selectedProducts);
        
        if (response.data.success) {
          // 削除成功時はメッセージを表示
          alert(response.data.message);
          
          // 削除された商品は表示されないので、蓄積リストから削除する
          this.allProducts = this.allProducts.filter(product => !this.selectedProducts.includes(product.id));
          
          // ページング情報を更新
          this.totalCount = this.allProducts.length;
          
          // 選択状態をリセット
          this.selectedProducts = [];
          
          // 現在のページに表示する商品を更新
          this.updateCurrentPageProducts();
        } else {
          // 削除失敗時はエラーメッセージを表示
          alert('商品の削除に失敗しました: ' + response.data.message);
        }
      } catch (error) {
        console.error('削除エラー:', error);
        alert('商品の削除中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // 価格変更モーダルを表示
    async showPriceChangeModal() {
      // 選択された商品がない場合は処理を中止
      if (this.selectedProducts.length === 0) {
        alert('価格変更する商品を選択してください');
        return;
      }
      
      // モーダルの表示とフォームのリセット
      this.selectedPriceChangeType = 'percent';
      this.priceChangeValue = '';
      this.priceChangeModalVisible = true;
    },
    
    // 価格変更モーダルを閉じる
    closePriceChangeModal() {
      this.priceChangeModalVisible = false;
      this.selectedPriceChangeType = '';
      this.priceChangeValue = '';
    },
    
    // 価格変更の確認
    async confirmPriceChange() {
      // バリデーションチェック
      
      // 値下げ区分が選択されていない場合
      if (!this.selectedPriceChangeType) {
        alert('値下げ区分（％または円）を選択してください');
        return;
      }
      
      // 変更値が入力されていない場合
      if (this.priceChangeValue === '' || this.priceChangeValue === null) {
        alert('変更値を入力してください');
        return;
      }
      
      // ％の場合は-100〜-1の範囲チェック
      if (this.selectedPriceChangeType === 'percent') {
        const value = parseInt(this.priceChangeValue);
        if (value < -100 || -1 < value) {
          alert('％の場合は-100〜-1の範囲で入力してください');
          return;
        }
      }
      
      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 選択された商品が自店舗のものかチェック
        const nonOwnStoreProducts = this.products.filter(product => 
          this.selectedProducts.includes(product.id) && 
          product.currentStoreId !== this.userStoreId
        );
        
        if (0 < nonOwnStoreProducts.length) {
          alert('自店舗の商品のみ価格変更できます。他店舗の商品が選択されています。');
          return;
        }
      }
      
      // 確認ダイアログを表示
      const confirmMessage = this.selectedPriceChangeType === 'percent' 
        ? `選択した商品の価格を${this.priceChangeValue}％変更します。よろしいですか？`
        : `選択した商品の価格を${this.priceChangeValue}円変更します。よろしいですか？`;
        
      const isConfirmed = confirm(confirmMessage);
      if (!isConfirmed) {
        return;
      }
      
      try {
        showLoading();
        // リクエストオブジェクトを作成
        const priceUpdateData = {
          productIds: this.selectedProducts,
          changeType: this.selectedPriceChangeType,
          changeValue: this.priceChangeValue
        };
        
        // 価格変更APIを呼び出し
        const response = await service.updateProductsPrice(priceUpdateData);
        
        if (response.data.success) {
          // 更新成功時はメッセージを表示
          alert(response.data.message);
          
          // 価格変更された商品の情報を一括で更新するために新しいAPIを呼び出し
          try {
            const productResponse = await service.getProductsByIds(this.selectedProducts);
            if (productResponse.data.success && 0 < productResponse.data.products.length) {
              const updatedProducts = this.formatProductsForDisplay(productResponse.data.products);
              
              // 蓄積リスト内の該当商品を一括で更新
              for (const updatedProduct of updatedProducts) {
                const index = this.allProducts.findIndex(p => p.id === updatedProduct.id);
                if (index !== -1) {
                  this.allProducts[index] = updatedProduct;
                }
              }
            }
          } catch (error) {
            console.error('商品情報新エラー:', error);
            throw new Error('商品情報の更新に失敗しました');
          }
          
          // モーダルを閉じる
          this.closePriceChangeModal();
          // 選択状態をリセット
          this.selectedProducts = [];
          // 現在のページに表示する商品を更新
          this.updateCurrentPageProducts();
        } else {
          // 更新失敗時はエラーメッセージを表示
          alert('価格の更新に失敗しました: ' + response.data.message);
        }
      } catch (error) {
        console.error('価格更新エラー:', error);
        alert('価格の更新中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // 一括公開処理
    async bulkPublish() {
      if (this.selectedProducts.length === 0) {
        alert('公開登録する商品を選択してください');
        return;
      }

      // 公開ステータス選択モーダルを表示
      this.selectedPublicStatusId = null;
      this.publicStatusModalVisible = true;
    },
    
    // 公開ステータス選択モーダルを閉じる
    closePublicStatusModal() {
      this.publicStatusModalVisible = false;
      this.selectedPublicStatusId = null;
    },
    
    // 公開ステータス変更の確認
    async confirmPublicStatusChange() {
      // 公開ステータスが選択されていない場合
      if (this.selectedPublicStatusId === null) {
        alert('公開ステータスを選択してください');
        return;
      }

      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 選択された商品が自店舗のものかチェック
        const nonOwnStoreProducts = this.products.filter(product => 
          this.selectedProducts.includes(product.id) && 
          product.currentStoreId !== this.userStoreId
        );
        
        if (0 < nonOwnStoreProducts.length) {
          alert('自店舗の商品のみ公開登録できます。他店舗の商品が選択されています。');
          return;
        }
      }
      
      const isConfirmed = confirm('選択した商品の公開ステータスを変更します。よろしいですか？');
      if (!isConfirmed) {
        return;
      }

      try {
        showLoading();
        // 公開ステータスIDを含めて一括公開APIを呼び出し
        const response = await service.bulkPublishProducts(this.selectedProducts, this.selectedPublicStatusId, this.selectedSalesChannelId);
        if (!response.data.success) {
          alert('公開登録に失敗しました');
          return;
        }
        alert(response.data.message);

        // 公開登録された商品の情報を一括で更新するために新しいAPIを呼び出し
        try {
          const productResponse = await service.getProductsByIds(this.selectedProducts);
          if (productResponse.data.success && productResponse.data.products.length > 0) {
            const updatedProducts = this.formatProductsForDisplay(productResponse.data.products);

            // 蓄積リスト内の該当商品を一括で更新
            for (const updatedProduct of updatedProducts) {
              const index = this.allProducts.findIndex(p => p.id === updatedProduct.id);
              if (index !== -1) {
                this.allProducts[index] = updatedProduct;
              }
            }
          }
        } catch (error) {
          console.error('商品情報新エラー:', error);
          throw new Error('商品情報の更新に失敗しました');
        }

        // モーダルを閉じる
        this.closePublicStatusModal();
        this.selectedProducts = [];
        this.updateCurrentPageProducts();
      } catch (error) {
        alert('公開登録中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // ランク変更モーダルを表示
    async showRankChangeModal() {
      // 選択された商品がない場合は処理を中止
      if (this.selectedProducts.length === 0) {
        alert('ランク変更する商品を選択してください');
        return;
      }
      
      try {
        showLoading();
        // ランク一覧を取得
        const response = await service.getRankList();
        this.ranks = response.data.ranks;
        this.selectedRankId = null; // 選択をリセット
        this.rankChangeModalVisible = true;
      } catch (error) {
        console.error('ランク一覧取得エラー:', error);
        alert('ランク一覧の取得中にエラーが発生しました');
      }  finally {
        hideLoading();
      }

    },
    
    // ランク変更モーダルを閉じる
    closeRankChangeModal() {
      this.rankChangeModalVisible = false;
      this.selectedRankId = null;
    },
    
    // ランク変更の確認
    async confirmRankChange() {
      // ランクが選択されていない場合
      if (this.selectedRankId === null) {
        alert('ランクを選択してください');
        return;
      }
      
      // 選択された商品のカテゴリIDを取得
      const selectedProductCategories = this.products
        .filter(product => this.selectedProducts.includes(product.id))
        .map(product => product.categoryId)
        .filter(categoryId => categoryId !== undefined && categoryId !== null);
      
      // 選択されたランクが各商品のカテゴリで有効かチェック
      const invalidProducts = [];
      
      for (const categoryId of selectedProductCategories) {
        // カテゴリに対応するランク一覧を取得
        const validRanks = this.getValidRanksForCategory(categoryId);
        
        // 選択されたランクがこのカテゴリで有効でない場合
        if (!validRanks.includes(parseInt(this.selectedRankId))) {
          const product = this.products.find(p => p.categoryId === categoryId);
          invalidProducts.push(product);
        }
      }
      
      // 無効な商品がある場合はエラーメッセージを表示
      if (0 < invalidProducts.length) {
        alert('選択したランクを設定できない商品があります。');
        return;
      }
      
      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 選択された商品が自店舗のものかチェック
        const nonOwnStoreProducts = this.products.filter(product => 
          this.selectedProducts.includes(product.id) && 
          product.currentStoreId !== this.userStoreId
        );
        
        if (0 < nonOwnStoreProducts.length) {
          alert('自店舗の商品のみランク変更できます。他店舗の商品が選択されています。');
          return;
        }
      }
      
      // 確認ダイアログを表示
      const isConfirmed = confirm('選択した商品のランクを変更します。よろしいですか？');
      if (!isConfirmed) {
        return;
      }
      
      try {
        showLoading();
        // ランク更新APIを呼び出し
        const response = await service.updateProductsRank(this.selectedProducts, this.selectedRankId);
        
        if (response.data.success) {
          // 更新成功時はメッセージを表示
          alert(response.data.message);
          
          // 更新された商品の情報を一括で更新するために新しいAPIを呼び出し
          try {
            const productResponse = await service.getProductsByIds(this.selectedProducts);
            if (productResponse.data.success && 0 < productResponse.data.products.length) {
              const updatedProducts = this.formatProductsForDisplay(productResponse.data.products);
              
              // 蓄積リスト内の該当商品を一括で更新
              for (const updatedProduct of updatedProducts) {
                const index = this.allProducts.findIndex(p => p.id === updatedProduct.id);
                if (index !== -1) {
                  this.allProducts[index] = updatedProduct;
                }
              }
            }
          } catch (error) {
            console.error('商品情報の更新に失敗しました:', error);
          }
          
          // モーダルを閉じる
          this.closeRankChangeModal();
          // 選択状態をリセット
          this.selectedProducts = [];
          
          // 現在のページに表示する商品を更新
          this.updateCurrentPageProducts();
        } else {
          // 更新失敗時はエラーメッセージを表示
          alert('ランクの更新に失敗しました: ' + response.data.message);
        }
      } catch (error) {
        console.error('ランク更新エラー:', error);
        alert('ランクの更新中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // ラベル印刷処理
    async printLabels() {
      // 選択された商品がない場合は処理を中止
      if (this.selectedProducts.length === 0) {
        alert('印刷する商品を選択してください');
        return;
      }

      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 選択された商品が自店舗のものかチェック
        const nonOwnStoreProducts = this.products.filter(product => 
          this.selectedProducts.includes(product.id) && 
          product.currentStoreId !== this.userStoreId
        );
        
        if (0 < nonOwnStoreProducts.length) {
          alert('自店舗の商品のみラベル印刷できます。他店舗の商品が選択されています。');
          return;
        }
      }
      
      try {
        showLoading();
        
        // 選択された商品の詳細情報を取得
        const response = await service.getProductsByIds(this.selectedProducts);
        if (!response.data.success) {
          alert('商品情報の取得に失敗しました');
          return;
        }
        
        const products = response.data.products;
        
        // 印刷処理の確認
        const isConfirmed = confirm(`${products.length}件の商品ラベルを印刷します。よろしいですか？`);
        if (!isConfirmed) {
          return;
        }
        
        // 一括ラベル生成APIを呼び出し
        const labelResponse = await service.generateBulkProductLabels(this.selectedProducts);
        
      if (!labelResponse.data.success) {
          alert(`ラベル生成に失敗しました: ${labelResponse.data.message}`);
          return;
        }
        
        // ラベルサイズごとのBase64エンコードされたPDFデータを取得
        const labelSizeToPdfMap = labelResponse.data.labelSizeToPdfMap;
        
        // 印刷エージェントサービスを使用して印刷
        await printAgentService.printMultipleLabels(
          labelSizeToPdfMap, 
          `BulkLabels-${this.selectedProducts.length}items`
        );
      } catch (error) {
        console.error('ラベル印刷処理中にエラーが発生しました:', error);
        alert('ラベル印刷処理中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // カテゴリに対応する有効なランクIDリストを取得
    getValidRanksForCategory(categoryId) {
      if (!this.originalCategories || !categoryId) {
        return [];
      }
      
      // カテゴリツリーから該当するカテゴリを探す
      for (const parentCategory of this.originalCategories) {
        for (const childCategory of parentCategory.childCategories || []) {
          if (childCategory.id === parseInt(categoryId)) {
            // このカテゴリで有効なランクIDのリストを返す
            return (childCategory.ranks || []).map(rank => rank.id);
          }
        }
      }
      
      return [];
    },
    
    // 表示カラム変更時の処理
    onVisibleColumnsChange(columns) {
      this.visibleColumns = [...columns];
      // ローカルストレージに保存して次回アクセス時も設定を維持
      localStorage.setItem('productBarcodeVisibleColumns', JSON.stringify(columns));
    },
    
    // カラム選択モーダルの表示切替
    toggleColumnSelector() {
      this.columnSelectorVisible = !this.columnSelectorVisible;
    },
  },
  mounted() {
    // 初期データ取得
    this.getInitData();
    
    // 表示カラム設定の復元
    const savedColumns = localStorage.getItem('productBarcodeVisibleColumns');
    if (savedColumns) {
      try {
        this.visibleColumns = JSON.parse(savedColumns);
      } catch (e) {
        console.error('表示カラム設定の復元に失敗しました:', e);
        // デフォルトで全カラムを表示
        this.visibleColumns = this.selectableColumns.map(col => col.key);
      }
    } else {
      // デフォルトで全カラムを表示
      this.visibleColumns = this.selectableColumns.map(col => col.key);
    }
  },
  watch: {
    // 値下げ区分が変更されたときに値をリセット
    selectedPriceChangeType(newType, oldType) {
      if (newType !== oldType) {
        this.priceChangeValue = '';
      }
    },
    'searchParams.productCode'(newVal) {
      if (newVal && newVal.length === constants.PRODUCT_CODE_LENGTH) {
        this.search();
      }
    }
  },
  computed: {
    // ユーザー権限の取得
    userAuthority() {
      return this.$store.getters['auth/currentUser']?.authority || 3; // デフォルトは権限なし
    },
    // ユーザーの所属店舗ID
    userStoreId() {
      return this.$store.getters['auth/currentUser']?.storeId || null;
    },
    
    // 選択可能なカラム（選択UIに表示するカラム）
    selectableColumns() {
      // 'select'キーのカラムは表示設定から除外（常に表示）
      return this.columns.filter(column => column.key !== 'select');
    },
    
    // 全選択状態の計算
    isAllSelected() {
      return 0 < this.sortedProducts.length && this.selectedProducts.length === this.sortedProducts.length;
    },
    
    // ソート済み商品一覧
    sortedProducts() {
      if (!this.sortKey) {
        return this.products;
      }
      
      return [...this.products].sort((a, b) => {
        let aValue = a[this.sortKey];
        let bValue = b[this.sortKey];
        
        // マスタデータの場合は元のID値でソート
        if (this.currentSortOriginalKey) {
          aValue = a[this.currentSortOriginalKey];
          bValue = b[this.currentSortOriginalKey];
        }
        
        // 金額カラムの場合は通貨記号を削除して数値として比較
        const monetaryColumns = ['purchasePrice', 'currentSellingPrice', 'initialSellingPrice', 'soldPrice'];
        if (monetaryColumns.includes(this.sortKey)) {
          // 通貨記号と区切り文字を削除して数値に変換
          const parseMonetaryValue = (value) => {
            if (!value) return 0;
            return parseInt(String(value).replace(/[¥,]/g, ''));
          };
          
          const aNum = parseMonetaryValue(aValue);
          const bNum = parseMonetaryValue(bValue);
          return this.sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // 文字列の場合は大文字小文字を区別せずに比較
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return this.sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
        }
        
        // 数値の場合は単純に比較
        return this.sortOrder === 'asc'
        ? aValue > bValue ? 1 : -1
        : aValue < bValue ? 1 : -1;
      });
    },
    
    // 公開ステータスが「販売済」かどうかを判定
    isPublicStatusSold() {
      return this.selectedPublicStatusId === constants.ProductStatus.SOLD.id;
    }
  }
}
</script>

<style scoped>
/* ランク変更モーダルのスタイル */
.rank-selector-options {
  display: flex;
  justify-content: center;
  margin: 20px 0;
  flex-wrap: wrap;
}

.rank-selector-option {
  text-align: center;
  margin: 0 15px 10px 15px;
}

.bold-label {
  font-weight: bold;
  margin-left: 5px;
}

/* 公開ステータス選択モーダルのスタイル */
.public-status-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
}

/* 検索結果ヘッダーのスタイル */
.search-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

/* カラム選択モーダルのスタイル */
.column-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.column-selector-content {
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  width: 400px;
  max-width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.column-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.column-selector-header h4 {
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.column-selector-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.column-selector-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.column-selector-option input[type="checkbox"] {
  margin: 0;
}
</style>
